// WSOLA changes speech duration without resampling individual waveform cycles.
// It overlaps 40 ms frames and searches around each expected frame boundary
// for the best waveform match. This keeps pitch stable while changing tempo.
export class PitchPreservingTimeStretch {
    // WSOLA frame geometry. The synthesis hop is fixed; speed changes only
    // how far the next analysis frame advances through the source audio.
    private readonly windowSize: number;
    private readonly hop: number;
    private readonly search: number;
    private readonly window: Float32Array;

    // Reused working buffers. Keeping them here avoids allocations on the
    // real-time audio thread.
    private readonly overlap: Float32Array;
    private readonly rawOverlap: Float32Array;
    private readonly region: Float32Array;
    private readonly output: Float32Array;

    // Source chunks use absolute sample positions so old chunks can be removed
    // without changing the coordinates used by frame alignment.
    private queue: Float32Array[];
    private inputStart: number;
    private inputEnd: number;
    private position: number;

    // Position inside the currently synthesized output hop.
    private outputPos: number;
    private speed: number;
    private ready: boolean;

    constructor(speed: number, rate: number) {
        this.windowSize = Math.floor(0.04 * rate);
        this.hop = Math.floor(0.02 * rate);
        this.search = Math.floor(0.01 * rate);
        this.window = new Float32Array(this.windowSize);
        this.overlap = new Float32Array(this.hop);
        this.rawOverlap = new Float32Array(this.hop);
        this.region = new Float32Array(this.windowSize + this.search * 2);
        this.output = new Float32Array(this.hop);
        this.queue = [];
        this.inputStart = 0;
        this.inputEnd = 0;
        this.position = 0;
        this.outputPos = this.hop;
        this.ready = false;
        this.speed = 1;
        this.setSpeed(speed);

        // A periodic Hann window is constant-overlap-add at a 50% hop.
        for (let i = 0; i < this.window.length; i++) {
            this.window[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / this.window.length);
        }
    }

    setSpeed(speed: number): void {
        this.speed = Number.isFinite(speed) && speed > 0 ? speed : 1;
    }

    add(chunk: Float32Array): void {
        this.queue.push(chunk);
        this.inputEnd += chunk.length;
    }

    // Fade and discard the queued tail after an interruption.
    interrupt(fadeoutSamples: number): void {
        const left = this.queue.reduce((sum, chunk) => sum + chunk.length, 0);
        const fadeout = Math.min(left, fadeoutSamples);

        let fadeoutIndex = 0;
        let queueIndex = 0;
        for (; queueIndex < this.queue.length; queueIndex++) {
            for (let i = 0; i < this.queue[queueIndex].length; i++) {
                this.queue[queueIndex][i] *= Math.max(0, (fadeout - fadeoutIndex++) / fadeout);
            }
            if (fadeoutIndex >= fadeout) break;
        }

        this.queue = this.queue.slice(0, queueIndex + 1);
        this.inputEnd = this.inputStart +
            this.queue.reduce((sum, chunk) => sum + chunk.length, 0);
    }

    process(target: Float32Array): void {
        for (let i = 0; i < target.length; i++) {
            if (this.outputPos >= this.output.length) {
                if (!this.generate()) {
                    for (; i < target.length; i++) target[i] = 0;
                    break;
                }
            }
            target[i] = this.output[this.outputPos++];
        }
    }

    // Build one output hop by overlapping the previous frame tail with the
    // best-matching source frame around the expected position.
    private generate(): boolean {
        const expected = Math.round(this.position);
        const min = Math.max(this.inputStart, expected - this.search);
        const max = Math.min(expected + this.search, this.inputEnd - this.windowSize);
        if (max < min) return false;

        const count = max - min + this.windowSize;
        if (!this.copy(min, this.region, count)) return false;

        const start = this.align(expected, min, max) - min;
        for (let i = 0; i < this.hop; i++) {
            this.output[i] = this.overlap[i] + this.region[start + i] * this.window[i];
            this.rawOverlap[i] = this.region[start + i + this.hop];
            this.overlap[i] = this.rawOverlap[i] * this.window[i + this.hop];
        }

        this.outputPos = 0;
        this.ready = true;

        // A larger analysis hop consumes source audio faster; the synthesis
        // hop stays fixed, so output pitch does not follow playback speed.
        this.position += this.speed * this.hop;
        this.prune();
        return true;
    }

    // Find the nearby source position whose waveform best continues the
    // previous frame. This is the pitch-preserving part of WSOLA.
    private align(expected: number, min: number, max: number): number {
        if (!this.ready) return Math.max(min, Math.min(max, expected));

        let best = Math.max(min, Math.min(max, expected));
        let bestScore = -Infinity;

        const evaluate = (candidate: number) => {
            if (candidate < min || candidate > max) return;
            const score = this.correlation(candidate - min) -
                Math.abs(candidate - expected) * 1e-6;
            if (score > bestScore) {
                best = candidate;
                bestScore = score;
            }
        };

        // Use a coarse search first, then refine around the best match.
        for (let distance = 0; distance <= this.search; distance += 4) {
            evaluate(expected - distance);
            if (distance > 0) evaluate(expected + distance);
        }

        const coarse = best;
        for (let candidate = coarse - 3; candidate <= coarse + 3; candidate++) {
            evaluate(candidate);
        }

        return best;
    }

    // Normalized cross-correlation makes matching independent of amplitude.
    private correlation(offset: number): number {
        let product = 0;
        let previousPower = 0;
        let currentPower = 0;

        for (let i = 0; i < this.hop; i += 4) {
            const previous = this.rawOverlap[i];
            const current = this.region[offset + i];
            product += previous * current;
            previousPower += previous * previous;
            currentPower += current * current;
        }

        return product / Math.sqrt(previousPower * currentPower + 1e-12);
    }

    // Copy a continuous absolute sample range across queued network chunks.
    private copy(start: number, target: Float32Array, count: number): boolean {
        let skip = start - this.inputStart;
        let written = 0;

        for (const chunk of this.queue) {
            if (skip >= chunk.length) {
                skip -= chunk.length;
                continue;
            }

            const take = Math.min(count - written, chunk.length - skip);
            target.set(chunk.subarray(skip, skip + take), written);
            written += take;
            skip = 0;
            if (written === count) return true;
        }

        return false;
    }

    // Keep only the source history still needed by the alignment search.
    private prune(): void {
        const keep = Math.floor(this.position) - this.search;
        while (this.queue.length > 0 &&
               this.inputStart + this.queue[0].length <= keep) {
            this.inputStart += this.queue.shift()!.length;
        }
    }
}

// AudioWorklet runs in a separate global scope. Serializing the self-contained
// class keeps the algorithm readable and type-checked in this TypeScript file
// while embedding the exact same implementation into the worklet module.
export function TimeStretchCode(): string {
    return `const PitchPreservingTimeStretch = (${PitchPreservingTimeStretch.toString()});`;
}
